import React, {
  useContext,
  useEffect, useMemo,
  useReducer,
  useState,
} from 'react'
import qs from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'
import cn from 'classnames'
import styles from './StudentIdForm.module.sass'
import Typography from '../Typography'
import TextInput from '../TextInput'
import Button from '../Button'
import Hr from '../Hr'
import Grid from '../Grid'
import { CustomerContext } from '../../contexts/CustomerContext'
import {
  initialState,
  reducer,
  RECEIVE_STUDENT,
  RECEIVE_STUDENT_ID,
  RECEIVE_ERROR,
  RESET,
} from './reducer'
import StudentInfo from './StudentInfo'
import ErrorMessage from './ErrorMessage'
import Student from '../../models/Student'
import StudentImage from '../../models/StudentImage'
import Package from '../../models/Package'
import OrderPackage from '../../models/OrderPackage'
import { routes } from '../../routes'
import { TEXT } from './text'
import useLocalStorage from '../../hooks/useLocalStorage'
import useQueryString from '../../hooks/use-query-string'

const StudentIdForm = ({ app, isAppointments = false, route, school, schoolType = '', setHasPackage }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    studentId, student, isError,
  } = state
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const location = useLocation()
  const [studentImage, setStudentImage] = useState()
  const [apptPackage, setApptPackage] = useState()
  const [isUsingAccessCode, setIsUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const [isOrderingAhead] = useLocalStorage('orderingAhead')
  const { p: packageId, order, preorder } = useQueryString()
  const [loading, setLoading] = useState(true);

  let currentSchool = app ? customer.apptCart.school : customer.currentSchool

  if (school) currentSchool = school

  const findStudentImage = (e) => {
    e.preventDefault()

    if (!studentId || studentId.length === 0) return

    Student
      .where({
        school_id: currentSchool.id, studentId,
      })
      .all()
      .then((response) => {
        const students = response.data;
        if (students.length < 1) {
          dispatch({ type: RECEIVE_ERROR });
        } else {
          dispatch({ type: RECEIVE_STUDENT, payload: response.data[response.data.length - 1] });
        }
      })
      .catch(() => dispatch({ type: RECEIVE_ERROR }));
  };

  useEffect(() => {
    if (!app) return
    /* should look for Fall E1 package for elementary */
    /* should look for appointment package / sittings for seniors */
    if (packageId) {
      Package
        .find(packageId)
        .then((res) => {
          if (!res) {
            setHasPackage(false);
            setLoading(false);
            return
          }

          setApptPackage(res.data)
          setLoading(false);
        })
    } else {
      if (schoolType.toLowerCase().includes('elementary')) {
        Package
          .where({ schoolId: school.id, elementarySittings: true })
          .all()
          .then((res) => {
            if (res.data.length < 1) {
              setHasPackage(false);
              setLoading(false);
              return
            }

            setApptPackage(res.data[0])
            setLoading(false);
          })
      } else {
        Package
          .where({ schoolId: school.id, highSchoolSittings: true })
          .order({ shouldShowAppointments: false })
          .all()
          .then((res) => {
            if (res.data.length < 1) {
              setHasPackage(false);
              setLoading(false);
              return
            }

            setApptPackage(res.data[0])
            setLoading(false);
          })
      }
    }
  }, [app, school])

  const submitHandler = (e) => {
    e.preventDefault()

    if (apptPackage) {
      const orderPackage = new OrderPackage({
        package: apptPackage,
        packageId: apptPackage.id,
        cartId: customer.apptCart.id,
        student,
        studentId: student.id,
        isPreviewHidden: !isUsingAccessCode,
      })

      setCustomer({ ...customer, currentStudent: student, currentOrderPackage: orderPackage })
      if (route) {
        history.push(route)
      } else {
        history.push(routes.appointmentOptionSelection(apptPackage.id))
      }
    } else {
      setCustomer({ ...customer, currentStudent: student })
      const isPreorderingParam = isOrderingAhead ? '' : ''

      if (order === 'yearbook_order') {
        history.push(routes.yearbooks({ sid: student.studentId, sch: currentSchool.id }))
      } else {
        history.push(`/package_selection${studentImage && studentImage.packageId ? `/${studentImage.packageId}` : ''}${isPreorderingParam}`)
      }
    }
  }

  const onSubmit = student ? submitHandler : findStudentImage

  const inputChangeHandler = (e) => {
    const val = e.target.value

    if (val === '') dispatch({ type: RESET })
    else dispatch({ type: RECEIVE_STUDENT_ID, payload: val })
  }

  const DISPLAY_TEXT = useMemo(() => {
    if (order === 'yearbook_order') return TEXT.STUDENT_TEXT;

    if (currentSchool.accessCodeOnly && !isOrderingAhead) return TEXT.ACCESS_CODE_ONLY;

    return TEXT.DEFAULT;
  }, [order, currentSchool.accessCodeOnly, isOrderingAhead])

  const infoEntryParams = qs.stringify({ order, app, preorder })

  if (app && !apptPackage && !loading) {
    return <div style={{ fontWeight: 'bold', textAlign: 'center' }}>There are no appointments available for your school at this time.</div>;
  }

  return (
    <form onSubmit={onSubmit}>
      <Grid container classes={styles.formInfo} maxWidth="md" margin="auto">
        {(!app || location.pathname.includes('student_id_entry')) && (
          <Grid item xs={12} classes={styles.instructions}>
            <Typography align="center" tag="h3" weight="bold" classes={cn(styles.shoobBlueText, styles.mb)}>
              {DISPLAY_TEXT.title}
            </Typography>
            <Typography align="center" weight="bold" classes={styles.mb}>
              {DISPLAY_TEXT.helpPartOne}
              <br />
              {DISPLAY_TEXT.helpPartTwo}
            </Typography>
            {order === 'yearbook_order' && (
              <Typography align="center" tag="h3" weight={600} classes={styles.blueText}>
                Staff Members - Please continue without Student ID Number
              </Typography>
            )}
          </Grid>
        )}
        <Grid item xs={12} classes={styles.mbLarge}>
          <TextInput
            name="student-id"
            val={studentId}
            onChange={inputChangeHandler}
            label={DISPLAY_TEXT.inputLabel}
            error={isError}
          />
        </Grid>
        {student && <StudentInfo student={student} accessCodeOnly={isUsingAccessCode} />}
        {isError && <ErrorMessage text={DISPLAY_TEXT.ERROR_MESSAGE} />}
        {isUsingAccessCode && student ? (
          <div />
        ) : (
          <>
            <Grid item xs={12} classes={!app ? styles.mbLarge : ''}>
              <Button
                name="student-search-btn"
                variant="primary"
                fullWidth
                size="large"
              >
                {student ? 'Continue' : 'Search for Student'}
              </Button>
            </Grid>
            {(!app || schoolType === 'Other') && (
              <>
                <Grid item xs={12}>
                  <Hr content="or" />
                </Grid>
                <Grid item xs={12} classes={!app ? styles.mbLarge : ''}>
                  <Button
                    name="student-info-entry-btn"
                    fullWidth
                    variant="primary"
                    size="large"
                    onClick={() => history.push(`/student_info_entry${infoEntryParams.length > 0 ? `?${infoEntryParams}` : ''}`)}
                  >
                    {DISPLAY_TEXT.infoEntryText}
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </form>
  )
}

export default StudentIdForm
